import { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { injectIntl } from 'react-intl';

import Orders from 'src/components/Orders';
import SubmitOrderButton from 'src/components/SubmitOrderButton';
import CloseBillButton from 'src/components/CloseBillButton';

import theme from 'src/styles/components/OrderBar.module.scss';

class OrderBar extends Component {
    canMove = true;

    allowedTime = 300;

    allowedDist = 200;

    initialTransform = undefined;

    lastTransformState = undefined;

    isExpanded = false;

    constructor(props) {
        super(props);

        this.state = {
            isOrdersExpanded: false
        };
    }

    componentDidMount() {
        this.initialTransform = this.barContainer.style.transform;
        this.lastTransformState = this.initialTransform;

        if (this.props.isDynamicSwipe) {
            this.allowedDist = this.barContainer.clientHeight / 3;
        }

        this.lastOverflowStyle = document.body.style.overflow;
    }

    handleMouseDown = (e) => {
        this.prepareSwipeParameters(e);
    }

    handleMouseUp = (e) => {
        e.preventDefault();

        this.processSwipe(e);
    }

    handleMouseLeave = (e) => {
        if (this.isMoveActive) {
            this.processSwipe(e);
        }
    }

    handleMouseMove = (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (this.isMoveActive) {
            this.moveContainer(e);
        }
    }

    handleTouchStart = (e) => {
        if (this.canMove) {
            if (e.cancelable) {
                e.preventDefault();
            }

            const touchobj = e.changedTouches[0];

            this.prepareSwipeParameters(touchobj);
        }
    }

    handleTouchMove = (e) => {
        const touchobj = e.changedTouches[0];

        this.moveContainer(touchobj);

        e.stopPropagation();
    }

    handleTouchEnd = (e) => {
        const touchobj = e.changedTouches[0];

        this.processSwipe(touchobj);
    }

    handleScrollStart = () => {
        const scrollContainer = this.scrollContainer || this.barContainer;

        const isScrolledToTop = scrollContainer.scrollTop === 0;

        if (!isScrolledToTop) {
            this.canMove = false;
        } else {
            this.canMove = true;
        }
    }

    prepareSwipeParameters = (event) => {
        this.shouldSwipe = false;
        this.dist = 0;

        if (!this.baseStartX) {
            this.baseStartX = event.clientX;
            this.baseStartY = event.clientY;
        }

        this.startX = event.clientX;
        this.startY = event.clientY;

        this.startTime = new Date().getTime();
        this.isMoveActive = true;

        this.barContainer.classList.add(theme.noTransition);
    }

    processSwipe = (target) => {
        this.dist = target.clientY - this.startY - this.scrollContainer.clientHeight;

        const isTopDirection = (target.clientY - this.startY) < 0;

        this.elapsedTime = new Date().getTime() - this.startTime;
        this.isMoveActive = false;

        const isSwipe = (this.elapsedTime <= this.allowedTime && !this.isExpanded && isTopDirection)
            || (this.elapsedTime <= this.allowedTime && this.isExpanded && !isTopDirection)
            || (isTopDirection && this.dist <= -1 * this.allowedDist && !this.isExpanded)
            || (!isTopDirection && this.dist >= this.allowedDist && this.isExpanded);

        if (isSwipe) {
            this.isExpanded = isTopDirection;
            this.swipeContainer(this.isExpanded);
        } else {
            this.transformContainer();
        }
        this.barContainer.classList.remove(theme.noTransition);
    }

    transformContainer = () => {
        if (document.body.style.overflow !== 'hidden') {
            this.lastOverflowStyle = document.body.style.overflow;
        }

        document.body.style.overflow = this.isExpanded ? 'hidden' : this.lastOverflowStyle;

        this.barContainer.style.transform = this.lastTransformState;
    }

    swipeContainer = (isTopDirection) => {
        if (document.body.style.overflow !== 'hidden') {
            this.lastOverflowStyle = document.body.style.overflow;
        }

        document.body.style.overflow = this.isExpanded ? 'hidden' : this.lastOverflowStyle;

        if (isTopDirection) {
            let scrollTo = window.innerHeight - this.scrollContainer.clientHeight;

            if (this.props.isDynamicSwipe && this.barContainer.clientHeight < scrollTo) {
                scrollTo = this.barContainer.clientHeight;
            }

            this.setState({
                isOrdersExpanded: true
            });
            this.barContainer.style.transform = `translate3d(0, -${scrollTo}px, 0)`;
        } else {
            this.setState({
                isOrdersExpanded: false
            });
            this.barContainer.style.transform = this.initialTransform;
        }

        this.lastTransformState = this.barContainer.style.transform;
    }

    moveContainer = (event) => {
        const dist = (event.clientY - this.baseStartY) - this.scrollContainer.clientHeight;

        if (dist >= 0) {
            return;
        }

        this.barContainer.style.transform = `translate3d(0, ${dist}px, 0)`;
    }

    render() {
        const { isActive } = this.props;
        const { isOrdersExpanded } = this.state;

        return (
            <div
              className={classnames(theme.container, { [theme.isActive]: isActive })}
              ref={(ref) => { this.barContainer = ref; }}
            >
                <div
                  className={classnames(theme.bar, { [theme.barExpanded]: !isOrdersExpanded })}
                  onTouchStart={this.handleTouchStart}
                  onTouchEnd={this.handleTouchEnd}
                  onTouchMove={this.handleTouchMove}
                  onMouseDown={this.handleMouseDown}
                  onMouseUp={this.handleMouseUp}
                  onMouseMove={this.handleMouseMove}
                  onMouseLeave={this.handleMouseLeave}
                  role="presentation"
                  ref={(ref) => { this.scrollContainer = ref; }}
                >
                    <div className={theme.actionBar} />
                </div>
                <div className={theme.order}>
                    <Orders />
                </div>
                <div className={theme.footer}>
                    <div className={theme.barContainer}>
                        <div className={theme.billContainer}>
                            <CloseBillButton />
                        </div>
                        <SubmitOrderButton />
                    </div>
                </div>
            </div>
        );
    }
}

OrderBar.propTypes = {
    isDynamicSwipe: PropTypes.bool,
    isActive: PropTypes.bool
};

OrderBar.defaultProps = {
    isDynamicSwipe: false,
    isActive: true
};

export default injectIntl(OrderBar);
